import { configureStore, ThunkAction, Action } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import uiSliceReducer from '@cloudflare-monorepo/ui/store/ui.ts';
import type { ApiClients } from '../../common/providers/ApiClientProvider.tsx';

// --- Define the Shape of the Extra Argument ---
// It will hold the API clients, initially null.
export interface ThunkExtraArgument {
  apiClients: ApiClients | null;
}

// --- Create the Mutable Placeholder Object ---
// This object is created once when the module loads and is passed to the store.
// ApiClientProvider will update its 'apiClients' property later.
export const thunkExtraArg: ThunkExtraArgument = {
  apiClients: null,
};

// --- Configure the Store ---
const store = configureStore({
  reducer: {
    ui: uiSliceReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: thunkExtraArg,
      },
    }),
});

// --- TypeScript Types for Redux ---
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/**
 * Defines a standard AppThunk type for async actions,
 * including our specific ThunkExtraArgument type.
 */
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  ThunkExtraArgument, // Specify our extra argument type
  Action<string>
>;

// --- Typed Hooks (no changes needed here) ---
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
